import { ToolBase, ToolPointer, ShiftLock } from './tool';
import { SnapResult } from '../inference/inference';
import { SNAP_COLORS, AXIS_COLORS } from '../render/preview';
import { Vec3, vec, AXIS_Z, projectPointOnPlane, dot, sub, cross, normalize, nearestAxis } from '../core/vec';
import { addLineSegment, faceFromRing } from '../core/lines';

/** Basis für Werkzeuge, die in einer Ebene (Boden oder Fläche) geschlossene Formen zeichnen. */
export abstract class PlaneToolBase extends ToolBase {
  protected plane: { point: Vec3; normal: Vec3 } | null = null;
  protected planeFaceId: number | null = null;
  /** Ebene kam von einer Fläche → fest; sonst wählt pickPlane sie dynamisch (waagrecht/senkrecht). */
  private planeFromFace = false;
  /** Pfeiltasten-Sperre der Zeichenebene: Normale rot (0), grün (1), blau (2). */
  protected lockedPlane: 0 | 1 | 2 | null = null;
  /** Umschalt gehalten → aktuelle Zeichenebene bleibt gesperrt. */
  private shiftPlane = new ShiftLock<0 | 1 | 2>();
  protected snap: SnapResult | null = null;
  /** Ableitungslinien der letzten Ausrichtung (Quelle → Punkt), werden als gepunktete Linien gezeigt. */
  protected inferenceLines: { from: Vec3; to: Vec3 }[] = [];

  protected getSnap(e: ToolPointer): SnapResult {
    const s = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: this.plane });
    this.snap = s;
    return s;
  }

  protected showSnapDot(s: SnapResult): void {
    if (s.kind !== 'ground') {
      this.ctx.preview.dot(s.point, SNAP_COLORS[s.kind] ?? 0x333333, s.priority >= 4 ? 11 : 8);
    }
  }

  /**
   * Zweiter Punkt einer Form (Diagonalecke, Radius …) in der Zeichenebene — mit Fang wie SketchUp:
   * 1. Eckpunkt/Mittelpunkt/Kante/Hilfslinie/Raster/Ursprung unter dem Cursor → exakt dorthin
   *    (senkrecht in die Zeichenebene projiziert),
   * 2. sonst Ausrichtung: liegt der Cursor auf der Flucht eines Eckpunkts (gleiche u- bzw.
   *    v-Koordinate), rastet die Koordinate ein — so fluchtet die Kante mit anderen Kanten.
   */
  protected snapInPlane(e: ToolPointer, snap: SnapResult, exclude: Vec3[] = []): Vec3 {
    this.inferenceLines = [];
    const f = this.frame();
    const toPlane = (p: Vec3): Vec3 => sub(p, mulN(f.n, dot(sub(p, f.origin), f.n)));
    const free = this.projectToPlane(e.ray) ?? toPlane(snap.point);

    const uOf = (p: Vec3) => dot(sub(p, f.origin), f.u);
    const vOf = (p: Vec3) => dot(sub(p, f.origin), f.v);
    // Koordinaten der Startpunkte: Ausrichtung darauf würde die Form flach drücken
    const exU = exclude.map((x) => uOf(toPlane(x)));
    const exV = exclude.map((x) => vOf(toPlane(x)));
    const collapses = (val: number, ex: number[]) => ex.some((x) => Math.abs(x - val) < 1e-6);

    if (DISCRETE_SNAPS.has(snap.kind)) {
      const q = toPlane(snap.point);
      // Rasterpunkt, der die Form auf 0 Breite/Höhe ziehen würde, ignorieren
      const flat = snap.kind === 'grid' && (collapses(uOf(q), exU) || collapses(vOf(q), exV));
      if (!flat && this.ctx.camera.screenDistance(q, e.x, e.y) <= SNAP_PX + 2) return q;
    }

    const at = (u: number, v: number): Vec3 => add3(f.origin, add3(mulN(f.u, u), mulN(f.v, v)));
    let u = uOf(free);
    let v = vOf(free);
    let bestU: { val: number; src: Vec3; d: number } | null = null;
    let bestV: { val: number; src: Vec3; d: number } | null = null;
    const sources: Vec3[] = [vec()];
    for (const vx of this.ctx.model.vertices.values()) sources.push(vx.p);
    for (const src of sources) {
      const q = toPlane(src);
      const qu = uOf(q), qv = vOf(q);
      const dU = this.ctx.camera.screenDistance(at(qu, v), e.x, e.y);
      if (dU <= ALIGN_PX && !collapses(qu, exU) && (!bestU || dU < bestU.d)) bestU = { val: qu, src, d: dU };
      const dV = this.ctx.camera.screenDistance(at(u, qv), e.x, e.y);
      if (dV <= ALIGN_PX && !collapses(qv, exV) && (!bestV || dV < bestV.d)) bestV = { val: qv, src, d: dV };
    }
    if (bestU) u = bestU.val;
    if (bestV) v = bestV.val;
    const p = at(u, v);
    if (bestU) this.inferenceLines.push({ from: bestU.src, to: p });
    if (bestV) this.inferenceLines.push({ from: bestV.src, to: p });
    return p;
  }

  /** Gepunktete Ableitungslinien in Achsfarbe (Fluchtrichtung) zeichnen. */
  protected showInference(): void {
    for (const l of this.inferenceLines) {
      const d = sub(l.to, l.from);
      if (Math.hypot(d.x, d.y, d.z) < 1e-6) continue;
      const near = nearestAxis(d);
      const color = near.angle < 0.01 ? AXIS_COLORS[near.axis] : 0x9a68d8;
      this.ctx.preview.line(l.from, l.to, color, true);
      this.ctx.preview.dot(l.to, color, 8);
    }
  }

  /** Erster Punkt: Flächenebene übernehmen, sonst waagrecht durch den Punkt (wie SketchUp). */
  protected adoptPlane(snap: SnapResult): void {
    if (snap.faceId !== undefined && snap.plane && this.lockedPlane === null) {
      this.plane = snap.plane;
      this.planeFaceId = snap.faceId;
      this.planeFromFace = true;
    } else {
      const n = [vec(1, 0, 0), vec(0, 1, 0), AXIS_Z][this.lockedPlane ?? 2]!;
      this.plane = { point: snap.point, normal: n };
      this.planeFaceId = null;
      this.planeFromFace = false;
    }
  }

  /**
   * Zeichenebene für den zweiten Punkt wählen, wenn der Start nicht auf einer Fläche lag:
   * - Pfeiltaste gesperrt → diese Achsebene,
   * - Maus bewegt sich auf dem Bildschirm entlang der blauen Achse → senkrechte Ebene
   *   (die rot/blau- oder grün/blau-Ebene, die der Kamera zugewandt ist),
   * - sonst waagrecht.
   */
  protected pickPlane(start: Vec3, e: ToolPointer): void {
    if (this.planeFromFace || !this.plane) return;
    let normalAxis: 0 | 1 | 2 = 2;
    const current = this.plane.normal;
    const currentAxis = (Math.abs(current.x) > 0.99 ? 0 : Math.abs(current.y) > 0.99 ? 1 : 2) as 0 | 1 | 2;
    const shiftAxis = this.shiftPlane.update(e.shift, () => currentAxis);
    if (this.lockedPlane !== null) {
      normalAxis = this.lockedPlane;
    } else if (shiftAxis !== null) {
      normalAxis = shiftAxis;
    } else {
      const s0 = this.ctx.camera.projectToScreen(start);
      const sz = this.ctx.camera.projectToScreen(add3(start, AXIS_Z));
      const bx = sz.x - s0.x, by = sz.y - s0.y;
      const mx = e.x - s0.x, my = e.y - s0.y;
      const bl = Math.hypot(bx, by), ml = Math.hypot(mx, my);
      const groundHit = projectPointOnPlane(e.ray.origin, e.ray.dir, start, AXIS_Z);
      const groundOk = groundHit !== null && dot(sub(groundHit, e.ray.origin), e.ray.dir) > 0;
      if (ml > 8 && bl > 1e-6) {
        const cos = Math.abs((bx * mx + by * my) / (bl * ml));
        // innerhalb ~25° der blauen Bildschirmrichtung → senkrecht zeichnen
        if (cos > Math.cos((25 * Math.PI) / 180) || !groundOk) {
          const view = this.ctx.camera.viewDirection();
          normalAxis = Math.abs(view.x) >= Math.abs(view.y) ? 0 : 1;
        }
      }
    }
    const n = [vec(1, 0, 0), vec(0, 1, 0), AXIS_Z][normalAxis]!;
    this.plane = { point: start, normal: n };
  }

  /** Pfeiltasten: → Ebene ⟂ rot, ← Ebene ⟂ grün, ↑ waagrecht, ↓ Sperre lösen (wie SketchUp). */
  keyDown(e: KeyboardEvent): void {
    const map: Record<string, 0 | 1 | 2> = { ArrowRight: 0, ArrowLeft: 1, ArrowUp: 2 };
    if (e.key === 'ArrowDown') this.lockedPlane = null;
    else if (map[e.key] !== undefined) this.lockedPlane = this.lockedPlane === map[e.key] ? null : map[e.key]!;
    else return;
    const names = ['senkrecht (Rot-Ebene gesperrt)', 'senkrecht (Grün-Ebene gesperrt)', 'waagrecht gesperrt'];
    this.ctx.ui.setStatus(this.lockedPlane === null ? 'Zeichenebene frei' : `Zeichenebene ${names[this.lockedPlane]}`);
  }

  /** 2D-Koordinatenrahmen der Zeichenebene: Boden → Welt-X/Y, Fläche → Flächensystem. */
  protected frame(): { origin: Vec3; u: Vec3; v: Vec3; n: Vec3 } {
    if (!this.plane) return { origin: vec(), u: vec(1, 0, 0), v: vec(0, 1, 0), n: AXIS_Z };
    const n = normalize(this.plane.normal);
    // u: Weltachse, die am wenigsten parallel zu n ist → stabil
    const candidates = [vec(1, 0, 0), vec(0, 1, 0), vec(0, 0, 1)];
    let u = candidates[0]!;
    let minAbs = Infinity;
    for (const c of candidates) {
      const d = Math.abs(dot(c, n));
      if (d < minAbs) { minAbs = d; u = c; }
    }
    u = normalize(sub(u, mulN(n, dot(u, n))));
    const v = cross(n, u);
    return { origin: this.plane.point, u, v, n };
  }

  protected projectToPlane(ray: { origin: Vec3; dir: Vec3 }): Vec3 | null {
    const plane = this.plane ?? { point: vec(), normal: AXIS_Z };
    return projectPointOnPlane(ray.origin, ray.dir, plane.point, plane.normal);
  }

  /** Geschlossene Form committen: auf bestehender Fläche teilen, sonst neu anlegen. */
  protected commitShape(points: Vec3[]): void {
    this.ctx.beginEdit();
    // Ring aus Linien aufbauen: teilt berührte Kanten/Flächen und bildet die Fläche (wie SketchUp)
    const faces: number[] = [];
    for (let i = 0; i < points.length; i++) {
      faces.push(...addLineSegment(this.ctx.model, points[i]!, points[(i + 1) % points.length]!).faceIds);
    }
    if (faces.length === 0) {
      // Rückfall: Kantensuche fand einen anderen Ring → Fläche direkt aus dem gezeichneten Ring
      // (nie über eine vorhandene Fläche legen; das prüft faceFromRing)
      faceFromRing(this.ctx.model, points);
    }
    this.ctx.requestUpdate();
  }

  protected resetPlane(): void {
    this.plane = null;
    this.planeFaceId = null;
    this.planeFromFace = false;
    this.shiftPlane.reset();
    // Pfeiltasten-Sperre gilt nur für die aktuelle Form (wie SketchUp)
    this.lockedPlane = null;
  }
}

const DISCRETE_SNAPS = new Set<SnapResult['kind']>(['endpoint', 'midpoint', 'onEdge', 'onGuide', 'origin', 'grid']);
const SNAP_PX = 12;
const ALIGN_PX = 8;

function add3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function mulN(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}
